import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { TenantsLeasesSubNav } from "@/components/TenantsLeasesSubNav";
import { EmptyState } from "@/components/ui/EmptyState";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, RenewalResource } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { RENEWAL_BOARD_COLUMNS, RENEWAL_STATUS_LABELS } from "@/lib/enum-labels";
import { formatMoneyCell } from "@/lib/format";
import { annualizedRentChangeCents } from "@/lib/rent-change";
import { showToast } from "@/lib/toast-queue";
import { useLoadingPhase } from "@/lib/use-loading-phase";

import { RenewalBoardCard } from "./RenewalBoardCard";

/**
 * `/tenants-leases/renewals` (Story 11.3) — PropDesk's first kanban-shaped
 * screen (spec Intent). A custom *view* over `GET /renewals` (Binding
 * AD-14, spec judgement call 1), not a new bespoke endpoint: fetched with
 * `within_6_months=true&page_size=200` — the real, server-side eligibility
 * window (`app/domain/renewals/board_window.py`) plus a single, generous
 * page (mirrors `PropertyRecord.tsx`'s/`DocumentsList.tsx`'s own
 * `page_size=200` "fetch everything a screen this size will ever hold in
 * one round trip" precedent — a family-scale portfolio's own renewal
 * pipeline is never going to need real pagination inside one kanban view).
 *
 * Columns are `RENEWAL_BOARD_COLUMNS`'s own fixed forward order — never
 * re-derived here. A card's status changes through its own tap-to-move
 * menu (`RenewalBoardCard.tsx`), which calls the real `PATCH /renewals/
 * {id}/status` route and writes the response straight into this query's
 * own cache (`moveCardInCache` below) — the card re-renders in its new
 * column immediately, with no full page reload and no full page refetch.
 */

const BOARD_QUERY_KEY = ["renewals", "board"] as const;
const BOARD_QUERY_STRING = "within_6_months=true&page_size=200";

export function RenewalBoard() {
  const queryClient = useQueryClient();
  const isOwner = useIsOwner();
  const [transitioningIds, setTransitioningIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    document.title = "Renewal board — PropDesk";
  }, []);

  const boardQuery = useQuery({
    queryKey: BOARD_QUERY_KEY,
    queryFn: () => apiFetch<Page<RenewalResource>>(`/renewals?${BOARD_QUERY_STRING}`),
  });

  const loadingPhase = useLoadingPhase(boardQuery.isPending);

  function moveCardInCache(updated: RenewalResource) {
    queryClient.setQueryData<Page<RenewalResource>>(BOARD_QUERY_KEY, (existing) => {
      if (existing === undefined) {
        return existing;
      }
      return { ...existing, data: existing.data.map((row) => (row.id === updated.id ? updated : row)) };
    });
  }

  async function fireTransition(renewalId: string, event: string) {
    setTransitioningIds((current) => new Set(current).add(renewalId));
    try {
      const updated = await apiFetch<RenewalResource>(`/renewals/${renewalId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event }),
      });
      moveCardInCache(updated);
      showToast(`Renewal moved to ${RENEWAL_STATUS_LABELS[updated.status] ?? updated.status}.`);
    } catch (error) {
      if (error instanceof ApiError) {
        showToast(error.message);
        return;
      }
      throw error;
    } finally {
      setTransitioningIds((current) => {
        const next = new Set(current);
        next.delete(renewalId);
        return next;
      });
    }
  }

  const rows = boardQuery.data?.data ?? [];

  const columns = useMemo(() => {
    const byStatus = new Map<string, RenewalResource[]>();
    for (const column of RENEWAL_BOARD_COLUMNS) {
      byStatus.set(column, []);
    }
    for (const row of rows) {
      byStatus.get(row.status)?.push(row);
    }
    // Most urgent (soonest lease end date) first within a column — a
    // presentation nicety only, never the board's own eligibility rule
    // (that stays server-side, `within_6_months`).
    for (const column of RENEWAL_BOARD_COLUMNS) {
      byStatus.get(column)?.sort((a, b) => (a.lease_end_date ?? "").localeCompare(b.lease_end_date ?? ""));
    }
    return byStatus;
  }, [rows]);

  // R-N6: the projected annualized rent-change roll-up, over every renewal
  // on the board with a real `proposed_rent_cents` set — never silently
  // treating one with no proposal yet as zero change (AC's own words).
  const withProposal = rows.filter((row) => row.proposed_rent_cents !== null && row.current_rent_cents !== null);
  const projectedAnnualChangeCents = withProposal.reduce(
    (sum, row) =>
      sum + annualizedRentChangeCents(row.current_rent_cents as number, row.proposed_rent_cents as number),
    0,
  );

  const errorMessage = boardQuery.error instanceof ApiError ? boardQuery.error.message : undefined;

  let content: ReactNode;
  if (boardQuery.isError) {
    content = (
      <div className="rounded-lg border border-border-hairline bg-surface p-6 text-body text-ink">
        <p>Couldn&apos;t load the renewal board.</p>
        {errorMessage ? <p className="mt-1 text-ink-secondary">{errorMessage}</p> : null}
      </div>
    );
  } else if (boardQuery.data === undefined) {
    // Nothing before 400ms; a skeleton between 400ms and 3s; the "unusual"
    // line after 3s — mirrors `DataTable.tsx`'s identical loading-phase
    // contract (`DESIGN.md`'s 400ms/3s rule).
    content =
      loadingPhase === "none" ? null : (
        <>
          <BoardColumns
            columns={null}
            isOwner={isOwner}
            transitioningIds={transitioningIds}
            onTransition={(id, event) => void fireTransition(id, event)}
          />
          {loadingPhase === "unusual" ? (
            <p className="mt-2 text-meta text-ink-secondary" role="status">
              Still loading. This is unusual at this size.
            </p>
          ) : null}
        </>
      );
  } else if (rows.length === 0) {
    content = (
      <EmptyState
        heading="Nothing needs renewing in the next 6 months."
        body="Every lease with a real end date on file expires further out than that, or there simply aren't any yet."
      />
    );
  } else {
    content = (
      <BoardColumns
        columns={columns}
        isOwner={isOwner}
        transitioningIds={transitioningIds}
        onTransition={(id, event) => void fireTransition(id, event)}
      />
    );
  }

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <TenantsLeasesSubNav />
      <div className="mb-2">
        <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Renewal board</h1>
        <p className="mt-2 text-body text-ink-secondary">
          Every renewal whose lease expires in the next 6 months, organized by where it stands.
        </p>
      </div>

      {rows.length > 0 ? (
        <p className="mb-4 text-meta text-ink-secondary">
          Projected annualized rent change:{" "}
          <span className="numeric-features text-money">
            {projectedAnnualChangeCents >= 0 ? "+" : ""}
            {formatMoneyCell(projectedAnnualChangeCents)}
          </span>{" "}
          — covers {withProposal.length} of {rows.length} renewal{rows.length === 1 ? "" : "s"} on the board with a
          proposed rent entered. The rest have no proposal yet and are not counted.
        </p>
      ) : null}

      {content}
    </div>
  );
}

/**
 * The column row itself — `columns === null` renders two skeleton blocks
 * per column instead of real cards (the loading phase; `RenewalBoard`'s own
 * `content` branch above never passes real card data alongside a skeleton
 * request, so this component never has to reconcile the two).
 */
function BoardColumns({
  columns,
  isOwner,
  transitioningIds,
  onTransition,
}: {
  columns: Map<string, RenewalResource[]> | null;
  isOwner: boolean;
  transitioningIds: ReadonlySet<string>;
  onTransition: (renewalId: string, event: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <div className="flex gap-4 pb-2">
        {RENEWAL_BOARD_COLUMNS.map((column) => {
          const columnRows = columns?.get(column) ?? [];
          return (
            <div key={column} className="w-board-column shrink-0 rounded-lg bg-surface-sunken p-3">
              <p className="mb-3 flex items-center justify-between text-label font-medium text-ink-secondary">
                <span>{RENEWAL_STATUS_LABELS[column] ?? column}</span>
                {columns !== null ? <span className="numeric-features">{columnRows.length}</span> : null}
              </p>
              <div className="flex flex-col gap-3">
                {columns === null
                  ? [0, 1].map((placeholder) => (
                      <div key={placeholder} className="min-h-board-card-min rounded-md bg-surface-sunken" />
                    ))
                  : columnRows.map((row) => (
                      <RenewalBoardCard
                        key={row.id}
                        renewal={row}
                        isOwner={isOwner}
                        transitioning={transitioningIds.has(row.id)}
                        onTransition={onTransition}
                      />
                    ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
